import { useEffect, useRef, useState } from "react";
import { AppNav } from "@/components/AppNav";
import { Overline } from "@/components/dash/primitives";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, RotateCcw, CheckCircle2, PiggyBank } from "lucide-react";
import * as staticData from "@/lib/freightRateData";
import { bunkerMonthly, bunkerLatest, bunkerSource } from "@/lib/bunkerData";
import { MonthYearTick } from "@/components/sections/BunkerChart";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const C = { sha: "#2a78d6", comp: "#1baf7a", xen: "#eda100", mna: "#4a3aa7" };

const dollars = (v) => (v < 0 ? "-$" : "$") + Math.abs(Math.round(v)).toLocaleString();

function RateTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 px-3.5 py-2.5 shadow-xl">
      <div className="font-num text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-xs text-slate-300">{p.name}</span>
          <span className="ml-auto font-num text-sm font-semibold text-white">
            {p.value == null ? "—" : dollars(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-2 text-xs text-slate-600">
          {it.dashed ? (
            <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: it.color }} />
          ) : (
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: it.color }} />
          )}
          {it.label}
        </span>
      ))}
    </div>
  );
}

const CardShell = ({ children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 mt-4">{children}</div>
);

const XT = { fill: "#64748b", fontSize: 10 };

const STATIC_BUNDLE = {
  rateSeries: staticData.rateSeries,
  avoidSeries: staticData.avoidSeries,
  cumulativeData: staticData.cumulativeData,
  termMeta: staticData.termMeta,
  grandTotal: staticData.grandTotal,
};

export default function CostAvoidance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const fileRef = useRef(null);
  const [remote, setRemote] = useState(null); // uploaded bundle (with .meta) or null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sc/rates").then(({ data }) => {
      if (data && Array.isArray(data.rateSeries) && data.rateSeries.length) setRemote(data);
    }).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sc/rates/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRemote({ ...data.bundle, meta: { filename: data.upload.filename, row_count: data.upload.row_count, updated_at: data.upload.created_at } });
      toast.success(`Charts updated from ${data.upload.row_count} weeks in "${data.upload.filename}".`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not read that spreadsheet.");
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    setBusy(true);
    try {
      await api.delete("/sc/rates");
      setRemote(null);
      toast.success("Reverted to the built-in dataset.");
    } catch {
      toast.error("Could not reset.");
    } finally {
      setBusy(false);
    }
  };

  const d = remote || STATIC_BUNDLE;
  const { rateSeries, avoidSeries, cumulativeData, termMeta, grandTotal } = d;

  const renderAvoidLabel = (props) => {
    const { x, y, value, index } = props;
    if (index !== avoidSeries.length - 1 || value == null) return null;
    const text = `Latest · ${dollars(value)}`;
    return (
      <g>
        <rect x={x - text.length * 6.2 - 14} y={y - 11} width={text.length * 6.2 + 10} height={22} rx={5} fill="#e6f1fb" />
        <text x={x - text.length * 6.2 - 9} y={y + 4} fontSize={11} fontFamily="'IBM Plex Mono', monospace" fill="#0c447c" fontWeight="600">{text}</text>
      </g>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNav />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <Overline>Interactive tool</Overline>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-3">
              Freight rate &amp; cost avoidance trends
            </h1>
            <p className="text-slate-600 mt-3 max-w-3xl leading-relaxed">
              Hover over any chart to see exact values for that week.
              {remote?.meta?.filename && (
                <span className="block mt-1 text-xs text-slate-400 font-num" data-testid="rates-source-stamp">
                  Data source: {remote.meta.filename} · {remote.meta.row_count} weeks
                  {remote.meta.updated_at ? ` · updated ${new Date(remote.meta.updated_at).toLocaleDateString()}` : ""}
                </span>
              )}
            </p>
          </div>

          {isAdmin && (
            <div className="flex items-center gap-2 shrink-0" data-testid="rates-admin-controls">
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="upload-rates-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-primary text-white text-sm font-medium px-4 py-2 hover:bg-primary/90 disabled:opacity-60 transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                {busy ? "Reading…" : "Upload weekly rates"}
              </button>
              {remote && (
                <button
                  onClick={onReset}
                  disabled={busy}
                  data-testid="reset-rates-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 text-sm px-3 py-2 hover:bg-slate-50 disabled:opacity-60"
                  title="Revert to the built-in dataset"
                >
                  <RotateCcw className="w-4 h-4" /> Reset
                </button>
              )}
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} />
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-500">
            {remote ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <UploadCloud className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />}
            <span>
              {remote ? "Charts are showing your uploaded spreadsheet." : "Charts are showing the built-in dataset."}
              {" "}Upload an .xlsx with columns: <span className="font-num">Date, SHA-SYD, COMPREHENSIVE (SHA-WORLD), Xeneta …, MNA NAC …, MKT NAC …, Cost Avoidance HWL NAC: MKT NAC, Cost Avoidance NAC: FAK Avg Market</span>.
            </span>
          </div>
        )}

        {/* EBS Quantum cost-avoidance highlight — visible to everyone */}
        <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50/70 p-5 sm:p-6" data-testid="ebs-cost-avoidance">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="shrink-0">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                <PiggyBank className="w-3.5 h-3.5" /> Cost avoidance
              </span>
              <div className="font-num text-4xl sm:text-5xl font-bold text-emerald-700 mt-2" data-testid="ebs-amount">$2,200</div>
            </div>
            <div className="sm:border-l sm:border-emerald-200 sm:pl-6">
              <h3 className="font-display text-lg font-bold text-slate-900">Reason: EBS Quantum</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed max-w-2xl">
                Hellmann (HWL) reviewed and reconciled the carrier&rsquo;s <strong>EBS (Emergency Bunker Surcharge) Quantum</strong>
                &nbsp;across the 2026 EBS circulars and secured an expected refund of <strong>$2,200</strong> on the Laem
                Chabang / Bangkok&nbsp;&rarr;&nbsp;Australia lane. This avoidance is in addition to the NAC-vs-market
                savings shown in the charts below.
              </p>
            </div>
          </div>
        </div>

        {/* Chart 1 — Weekly rate trends */}
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-12">Weekly rate trends</h2>
        <p className="text-sm text-slate-500 mt-1">SHA–SYD, Comprehensive (SHA–WORLD), Xeneta market rate, and MNA NAC benchmark</p>
        <CardShell>
          <Legend items={[
            { label: "SHA-SYD", color: C.sha },
            { label: "Comprehensive (SHA-WORLD)", color: C.comp },
            { label: "Xeneta market rate LCH-SYD", color: C.xen },
            { label: "MNA NAC (LCH-SYD)", color: C.mna, dashed: true },
          ]} />
          <ResponsiveContainer width="100%" height={420}>
            <LineChart data={rateSeries} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={XT} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={40} />
              <YAxis tickFormatter={(v) => `$${v.toLocaleString()}`} tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={60} />
              <Tooltip content={<RateTip />} />
              <Line type="monotone" dataKey="shaSyd" name="SHA-SYD" stroke={C.sha} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
              <Line type="monotone" dataKey="comprehensive" name="Comprehensive (SHA-WORLD)" stroke={C.comp} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
              <Line type="monotone" dataKey="xeneta" name="Xeneta market rate LCH-SYD" stroke={C.xen} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
              <Line type="monotone" dataKey="mnaNac" name="MNA NAC (LCH-SYD)" stroke={C.mna} strokeWidth={2} strokeDasharray="6 4" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardShell>
        <p className="text-xs text-slate-400 mt-2">Hover anywhere on the chart to see all series values for that week.</p>

        {/* Chart 2 — Cost avoidance trend */}
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-12">Cost avoidance trend</h2>
        <p className="text-sm text-slate-500 mt-1">Weekly cost avoidance vs. benchmark rate (HWL NAC vs MKT NAC) — negative = savings, positive = additional cost</p>
        <CardShell>
          <Legend items={[{ label: "Cost avoidance: HWL NAC vs MKT NAC", color: C.sha }]} />
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart data={avoidSeries} margin={{ top: 8, right: 90, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="avoidGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.sha} stopOpacity={0.12} />
                  <stop offset="100%" stopColor={C.sha} stopOpacity={0.12} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={XT} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={40} />
              <YAxis tickFormatter={dollars} tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={66} />
              <Tooltip content={<RateTip />} />
              <Area type="linear" dataKey="avoid" name="Cost avoidance: HWL NAC vs MKT NAC" stroke={C.sha} strokeWidth={2} fill="url(#avoidGrad)" dot={false} activeDot={{ r: 5 }}
                label={renderAvoidLabel} />
            </AreaChart>
          </ResponsiveContainer>
        </CardShell>
        <p className="text-xs text-slate-400 mt-2">Hover anywhere on the chart to see the value for that week. The latest value is labeled at the end of the line.</p>

        {/* Chart 3 — Cumulative cost avoidance by contract term */}
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-12">Cumulative cost avoidance by contract term</h2>
        <p className="text-sm text-slate-500 mt-1">Running total of NAC vs FAK average-market cost avoidance, reset at each April–March contract term — negative = net savings, positive = net additional cost</p>
        <p className="text-sm font-medium text-slate-800 mt-1" data-testid="ca-total-savings">
          Total net cost avoidance across all {avoidSeries.length} weeks shown: {dollars(grandTotal)}
        </p>
        <CardShell>
          <Legend items={termMeta.map((t) => ({ label: `${t.label}  ·  ${dollars(t.total)}`, color: t.color }))} />
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={cumulativeData} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={XT} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={40} />
              <YAxis tickFormatter={dollars} tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={72} />
              <Tooltip content={<RateTip />} />
              {termMeta.map((t) => (
                <Line key={t.key} type="linear" dataKey={t.key} name={t.label} stroke={t.color} strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardShell>
        <p className="text-xs text-slate-400 mt-2">Hover anywhere on the chart to see each term's running total for that week. Final totals are labeled in the legend above.</p>

        {/* BAF Monitoring — bunker fuel prices by port */}
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-12" data-testid="baf-monitoring">BAF monitoring — bunker fuel prices by port</h2>
        <p className="text-sm text-slate-500 mt-1">The Bunker Adjustment Factor (BAF) is driven by the 0.5% VLSFO fuel price. Tracking VLSFO by load port shows whether carrier BAF changes are justified by the underlying fuel cost — US$/tonne.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          {bunkerLatest.map((b) => (
            <div key={b.port} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`bunker-card-${b.port.toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: b.color }} />
                <span className="text-xs uppercase tracking-wider text-slate-500">{b.port}</span>
              </div>
              <div className="font-num text-2xl font-bold text-slate-900 mt-1">${b.vlsfo.toLocaleString()}<span className="text-sm font-medium text-slate-400">/t</span></div>
              <div className="flex gap-3 mt-1 text-xs font-num">
                <span className={b.mom < 0 ? "text-emerald-600" : "text-rose-600"}>{b.mom > 0 ? "▲" : "▼"} {Math.abs(b.mom)}% MoM</span>
                <span className={b.yoy < 0 ? "text-emerald-600" : "text-rose-600"}>{b.yoy > 0 ? "▲" : "▼"} {Math.abs(b.yoy)}% YoY</span>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">LSMGO ${b.lsmgo.toLocaleString()} · IFO380 ${b.ifo380.toLocaleString()}</div>
            </div>
          ))}
        </div>
        <CardShell>
          <Legend items={[...bunkerLatest.map((b) => ({ label: `${b.port} VLSFO`, color: b.color })), { label: "Singapore forecast (forward curve)", color: "#1baf7a", dashed: true }]} />
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={bunkerMonthly} margin={{ top: 8, right: 24, left: 8, bottom: 18 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} interval={0} height={44} tick={<MonthYearTick />} />
              <YAxis tickFormatter={(v) => `$${v}`} tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} width={56} domain={["auto", "auto"]} />
              <Tooltip content={<RateTip />} />
              <Line type="monotone" dataKey="rotterdam" name="Rotterdam VLSFO" stroke="#2a78d6" strokeWidth={2.25} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls={false} />
              <Line type="monotone" dataKey="singapore" name="Singapore VLSFO" stroke="#1baf7a" strokeWidth={2.25} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls={false} />
              <Line type="monotone" dataKey="houston" name="Houston VLSFO" stroke="#eda100" strokeWidth={2.25} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls={false} />
              <Line type="monotone" dataKey="singaporeF" name="Singapore VLSFO — forecast" stroke="#1baf7a" strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 4 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-slate-400 mt-3">Source: {bunkerSource}</p>
        </CardShell>
      </div>
    </div>
  );
}
